import { notifications } from '@mantine/notifications';
import { IconCheck, IconExclamationCircle } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type {
    BillingResponse,
    CreditSummaryResponse,
    HistoryBillResponse,
    UnsetResponse,
    UpdateBillingRequest,
} from '@xver/@types/bill';
import { getErrorMessage } from '@xver/utils/error';
import { useTranslation } from 'react-i18next';

export const BILLING_QUERY_KEY = ['apis.v1.billing.address'];
export const BILLING_UNSET_QUERY_KEY = ['apis.v1.billing.check-unset'];
export const BILLING_HISTORY_QUERY_KEY = ['apis.v1.billing.history'];
export const CREDIT_SUMMARY_QUERY_KEY = ['apis.v1.billing.credit-summary'];

export function useBillingQuery() {
    return useQuery<BillingResponse>({
        queryKey: BILLING_QUERY_KEY,
        queryFn: Endpoints.v1.Billing.getBilling,
    });
}

export function useBillingUnsetQuery() {
    return useQuery<UnsetResponse>({
        queryKey: BILLING_UNSET_QUERY_KEY,
        queryFn: Endpoints.v1.Billing.checkUnsetBilling,
    });
}

export function useCreditSummaryQuery() {
    return useQuery<CreditSummaryResponse>({
        queryKey: CREDIT_SUMMARY_QUERY_KEY,
        queryFn: Endpoints.v1.Billing.getCreditSummary,
        staleTime: 30_000,
    });
}

export function useBillingHistoryQuery(page: number, pageSize: number) {
    return useQuery<HistoryBillResponse>({
        queryKey: [...BILLING_HISTORY_QUERY_KEY, page, pageSize],
        queryFn: () => Endpoints.v1.Billing.getHistoryBill(page, pageSize),
    });
}

export function useUpdateBillingMutation() {
    const { t } = useTranslation('billings');
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload: UpdateBillingRequest) => Endpoints.v1.Billing.updateBilling(payload),
        onSuccess: async () => {
            notifications.show({
                title: t('toast.saveTitle'),
                message: t('toast.saveMessage'),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY }),
                queryClient.invalidateQueries({ queryKey: BILLING_UNSET_QUERY_KEY }),
            ]);
        },
        onError: (err: unknown) => {
            notifications.show({
                title: t('toast.errorTitle'),
                message: getErrorMessage(err) ?? t('toast.errorMessage'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });
}
